// SourceMenu — viewer brand-menu dropdown.
// Shell: AppBrandMenu (shared with Studio). Logic: file loading, theme/locale,
// export, share, compare. Share items use keepOpen to show inline status.

import {
  useCallback,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Vfs } from "@archlang/viewer-core";
import {
  File,
  FilePlus,
  Folder,
  RotateCcw,
  Download,
  Image,
  ArrowLeftRight,
  X,
  Copy,
  Code,
  Link,
  Share2,
  MoonStar,
  Languages,
} from "lucide-react";
import { useTheme, THEMES } from "../theme";
import { useTranslation, useLocale, LOCALES } from "../i18n";
import { fileListToVfs, type LoadedSources } from "../sourceLoader";
import { emptyProjectSources } from "../emptyProject";
import { zipStore } from "../zip";
import type { ShareOutcome } from "../shareLink";
import {
  AppBrandMenu,
  type AppBrandMenuItem,
  type AppBrandMenuSection,
} from "../workbench/primitives/AppBrandMenu";

function zipBaseName(label: string): string {
  const slug = label
    .trim()
    .replace(/\.[^.]+$/, "")
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "workspace";
}

export interface SourceSlot {
  readonly vfs: Vfs;
  readonly label: string;
}

export interface SourceMenuProps {
  readonly currentLabel: string;
  readonly currentSources?: SourceSlot;
  readonly onLoaded: (sources: LoadedSources) => void;
  readonly onResetDemo: () => void;
  /** Extra entries appended to the workspace section (an alternate surface's
   *  entry point, e.g. "Scratch mode"). */
  readonly extraWorkspaceItems?: readonly AppBrandMenuItem[];
  /** Hide the open/new/demo workspace section — for a surface that owns its
   *  own single-file content (the scratch sketch). */
  readonly hideWorkspaceSection?: boolean;
  /** What this menu calls the thing it acts on: "space" (default), "sketch". */
  readonly noun?: string;
  /** Wipe the current content back to its blank template. */
  readonly onReset?: () => void;
  readonly compareAgainst?: SourceSlot | null;
  readonly compareSwapped?: boolean;
  readonly onSetCompare?: (slot: SourceSlot | null) => void;
  readonly onSwapCompare?: () => void;
  readonly onClearCompare?: () => void;
  readonly variant?: "brand" | "icon" | "label";
  readonly triggerLabel?: string;
  /** Pill naming this surface next to the wordmark, e.g. "Scratch". */
  readonly brandTagline?: string;
  readonly onExportPng?: () => void | Promise<void>;
  readonly onExportSvg?: () => void | Promise<void>;
  readonly onCopyImage?: () => void | Promise<void>;
  readonly exportNoun?: string;
  readonly onShareWorkspace?: () => Promise<ShareOutcome> | ShareOutcome;
  readonly onShareView?: () => Promise<ShareOutcome> | ShareOutcome;
}

const ICON_PROPS = { size: 16, strokeWidth: 1.75, "aria-hidden": true } as const;

export function SourceMenu({
  currentLabel,
  currentSources,
  onLoaded,
  onResetDemo,
  extraWorkspaceItems,
  hideWorkspaceSection = false,
  noun = "space",
  onReset,
  compareAgainst = null,
  compareSwapped = false,
  onSetCompare,
  onSwapCompare,
  onClearCompare,
  variant = "brand",
  triggerLabel,
  brandTagline,
  onExportPng,
  onExportSvg,
  onCopyImage,
  exportNoun,
  onShareWorkspace,
  onShareView,
}: SourceMenuProps) {
  const { t } = useTranslation("sourceMenu");
  const { t: tCommon } = useTranslation("common");
  const [theme, setTheme] = useTheme();
  const themeIdx = Math.max(0, THEMES.findIndex((th) => th.id === theme));
  const nextTheme = THEMES[(themeIdx + 1) % THEMES.length]!;
  const { locale, setLocale } = useLocale();
  const localeIdx = Math.max(0, LOCALES.findIndex((l) => l.id === locale));
  const nextLocale = LOCALES[(localeIdx + 1) % LOCALES.length]!;

  const [status, setStatus] = useState<ShareOutcome | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);
  const compareFilesRef = useRef<HTMLInputElement | null>(null);
  const compareFolderRef = useRef<HTMLInputElement | null>(null);

  const NO_FILES_MSG = "No .arch or package.archspace files in that selection.";

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      setStatus(null);
      try {
        const loaded = await fileListToVfs(files);
        if (loaded.vfs.size === 0) {
          setStatus({ ok: false, message: NO_FILES_MSG });
          return;
        }
        onLoaded(loaded);
      } catch {
        setStatus({ ok: false, message: "Couldn't read those files." });
      }
    },
    [onLoaded],
  );

  const handleCompareFiles = useCallback(
    async (files: FileList | File[]) => {
      setStatus(null);
      try {
        const loaded = await fileListToVfs(files);
        if (loaded.vfs.size === 0) {
          setStatus({ ok: false, message: NO_FILES_MSG });
          return;
        }
        onSetCompare?.({ vfs: loaded.vfs, label: loaded.label });
      } catch {
        setStatus({ ok: false, message: "Couldn't read those files." });
      }
    },
    [onSetCompare],
  );

  const runAction = useCallback(async (fn: () => void | Promise<void>) => {
    setStatus(null);
    try {
      await fn();
    } catch (e) {
      setStatus({
        ok: false,
        message: e instanceof Error ? e.message : "Something went wrong.",
      });
    }
  }, []);

  // Share: keeps menu open via keepOpen; sets status for inline feedback.
  const runShare = useCallback(
    async (fn: () => Promise<ShareOutcome> | ShareOutcome) => {
      setStatus(null);
      try {
        setStatus(await fn());
      } catch (e) {
        setStatus({
          ok: false,
          message: e instanceof Error ? e.message : "Couldn't create a link.",
        });
      }
    },
    [],
  );

  const imageNoun = exportNoun ?? noun;
  const showCompareSection = Boolean(onSetCompare);
  const inDiff = compareAgainst !== null;

  // ── Build sections ──────────────────────────────────────────────────────────

  const sections: AppBrandMenuSection[] = [];

  if (onReset) {
    sections.push({
      items: [{ key: "reset", icon: <RotateCcw {...ICON_PROPS} />, label: t("resetSketch"), onClick: onReset }],
    });
  }

  if (!hideWorkspaceSection) {
    const wsItems: AppBrandMenuItem[] = [
      { key: "files", icon: <File {...ICON_PROPS} />, label: t("openFiles"), onClick: () => fileInputRef.current?.click() },
      { key: "folder", icon: <Folder {...ICON_PROPS} />, label: t("openFolder"), onClick: () => folderInputRef.current?.click() },
      { key: "new", icon: <FilePlus {...ICON_PROPS} />, label: t("newProject"), onClick: () => onLoaded(emptyProjectSources()) },
    ];
    if (extraWorkspaceItems) wsItems.push(...extraWorkspaceItems);
    wsItems.push({ key: "demo", icon: <RotateCcw {...ICON_PROPS} />, label: t("loadDemo"), onClick: onResetDemo });
    sections.push({ items: wsItems });
  }

  sections.push({
    label: t("appearance"),
    items: [
      { key: "theme", icon: <MoonStar {...ICON_PROPS} />, label: t("theme", { label: THEMES[themeIdx]?.label ?? theme }), onClick: () => setTheme(nextTheme.id) },
      { key: "lang", icon: <Languages {...ICON_PROPS} />, label: t("language", { label: LOCALES[localeIdx]?.label ?? locale }), onClick: () => void setLocale(nextLocale.id) },
    ],
  });

  const exportItems: AppBrandMenuItem[] = [];
  if (onCopyImage) exportItems.push({ key: "copy-img", icon: <Copy {...ICON_PROPS} />, label: t("copyImage", { noun: imageNoun }), onClick: () => void runAction(onCopyImage) });
  if (onExportPng) exportItems.push({ key: "png", icon: <Image {...ICON_PROPS} />, label: t("downloadPng", { noun: imageNoun }), onClick: () => void runAction(onExportPng) });
  if (onExportSvg) exportItems.push({ key: "svg", icon: <Code {...ICON_PROPS} />, label: t("downloadSvg", { noun: imageNoun }), onClick: () => void runAction(onExportSvg) });
  if (currentSources && currentSources.vfs.size > 0) {
    exportItems.push({
      key: "zip",
      icon: <Download {...ICON_PROPS} />,
      label: t("downloadZip", { noun }),
      onClick: () => {
        const bytes = zipStore(currentSources.vfs);
        const blob = new Blob([bytes as BlobPart], { type: "application/zip" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${zipBaseName(currentSources.label)}.zip`;
        a.click();
        URL.revokeObjectURL(url);
      },
    });
  }
  if (exportItems.length > 0) sections.push({ label: t("export"), items: exportItems });

  if (onShareWorkspace || onShareView) {
    const shareItems: AppBrandMenuItem[] = [];
    if (onShareWorkspace) {
      shareItems.push({ key: "link-ws", icon: <Link {...ICON_PROPS} />, label: t("copyLinkWorkspace"), keepOpen: true, onClick: () => void runShare(onShareWorkspace) });
    }
    if (onShareView) {
      shareItems.push({ key: "link-view", icon: <Share2 {...ICON_PROPS} />, label: t("copyLinkView"), keepOpen: true, onClick: () => void runShare(onShareView) });
    }
    sections.push({ label: t("share"), items: shareItems });
  }

  if (showCompareSection) {
    const compareLabel: ReactNode = (
      <>
        {t("compare")}
        {inDiff && (
          <span className="ml-1 rounded-full bg-arch-500/20 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-arch-300">
            {tCommon("active")}
          </span>
        )}
      </>
    );

    const compareItems: AppBrandMenuItem[] = [];
    if (inDiff) {
      compareItems.push({
        kind: "custom",
        key: "compare-summary",
        render: () => (
          <CompareSummary
            currentLabel={currentLabel}
            compareLabel={compareAgainst!.label}
            swapped={compareSwapped}
          />
        ),
      });
      compareItems.push({ key: "swap", icon: <ArrowLeftRight {...ICON_PROPS} />, label: "Swap before ↔ after", onClick: () => onSwapCompare?.() });
      compareItems.push({ key: "replace-files", icon: <File {...ICON_PROPS} />, label: "Replace target (files)…", onClick: () => compareFilesRef.current?.click() });
      compareItems.push({ key: "replace-folder", icon: <Folder {...ICON_PROPS} />, label: "Replace target (folder)…", onClick: () => compareFolderRef.current?.click() });
      compareItems.push({ key: "exit-diff", icon: <X {...ICON_PROPS} />, label: "Exit diff", variant: "danger", onClick: () => onClearCompare?.() });
    } else {
      compareItems.push({ key: "cmp-files", icon: <File {...ICON_PROPS} />, label: "Compare against files…", onClick: () => compareFilesRef.current?.click() });
      compareItems.push({ key: "cmp-folder", icon: <Folder {...ICON_PROPS} />, label: "Compare against folder…", onClick: () => compareFolderRef.current?.click() });
    }
    sections.push({ label: compareLabel, items: compareItems });
  }

  // ── Trigger variants ────────────────────────────────────────────────────────

  const triggerContent =
    variant === "icon" ? (
      <span className="text-lg font-bold text-arch-400" aria-label="Workspace menu">◆</span>
    ) : variant === "label" ? (
      <span className="text-sm font-medium text-slate-200">{triggerLabel ?? currentLabel}</span>
    ) : undefined;

  const triggerClassName =
    variant === "icon"
      ? "flex items-center rounded-md px-1.5 py-1 transition-colors hover:bg-slate-800/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-arch-400"
      : undefined;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      <AppBrandMenu
        tagline={brandTagline}
        triggerContent={triggerContent}
        triggerClassName={triggerClassName}
        sections={sections}
        header={
          <>
            <div className="border-b border-slate-700/60 px-3 py-2">
              <p className="truncate text-[11px] font-medium text-slate-500" title={currentLabel}>
                {currentLabel}
              </p>
            </div>
            {status && (
              <div
                className={`px-3 py-2 text-sm ${status.ok ? "bg-green-500/10 text-green-300" : "bg-red-500/10 text-red-400"}`}
                role={status.ok ? "status" : "alert"}
              >
                {status.message}
              </div>
            )}
          </>
        }
        footer={
          <div className="border-t border-slate-700/60 px-3 py-2 text-[11px] text-slate-600">
            drop files anywhere
          </div>
        }
        panelClassName="max-w-[320px]"
      />

      <input ref={fileInputRef} type="file" accept=".arch,.archspace" multiple hidden
        onChange={(e) => { if (e.target.files) void handleFiles(e.target.files); e.target.value = ""; }} />
      <input ref={folderInputRef} type="file"
        // @ts-expect-error — webkitdirectory isn't in React's typed attrs.
        webkitdirectory="" multiple hidden
        onChange={(e) => { if (e.target.files) void handleFiles(e.target.files); e.target.value = ""; }} />
      <input ref={compareFilesRef} type="file" accept=".arch,.archspace" multiple hidden
        onChange={(e) => { if (e.target.files) void handleCompareFiles(e.target.files); e.target.value = ""; }} />
      <input ref={compareFolderRef} type="file"
        // @ts-expect-error
        webkitdirectory="" multiple hidden
        onChange={(e) => { if (e.target.files) void handleCompareFiles(e.target.files); e.target.value = ""; }} />
    </>
  );
}

function CompareSummary({
  currentLabel,
  compareLabel,
  swapped,
}: {
  readonly currentLabel: string;
  readonly compareLabel: string;
  readonly swapped: boolean;
}) {
  const beforeLabel = swapped ? compareLabel : currentLabel;
  const afterLabel = swapped ? currentLabel : compareLabel;
  return (
    <div className="mx-3 mb-1 mt-0.5 rounded-md border border-slate-700/60 bg-slate-800/40 px-2 py-1.5 text-[11px]">
      <div className="flex items-center gap-1.5">
        <span className="w-8 shrink-0 font-semibold uppercase tracking-wide text-slate-600">before</span>
        <span className="truncate text-slate-300" title={beforeLabel}>{beforeLabel}</span>
      </div>
      <div className="mt-0.5 flex items-center gap-1.5">
        <span className="w-8 shrink-0 font-semibold uppercase tracking-wide text-slate-600">after</span>
        <span className="truncate text-slate-300" title={afterLabel}>{afterLabel}</span>
      </div>
    </div>
  );
}
